import {
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    type FocusEvent,
    type ForwardedRef,
    type KeyboardEvent,
    type MouseEvent,
} from "react";
import {
    insertAdToken,
    matchAdTokens,
    readAdTokenContext,
    type AdToken,
    type AdTokenContext,
    type AdTokenMatch,
} from "./adTokens";

/**
 * The ad token picker's behavior, with no field and no menu in it (MPN-1932, MPN-1933).
 *
 * The trigger is the bracket itself rather than a button: the bracket is what a writer
 * already types, and a menu at the caret is the only place the choice is actually being made.
 *
 * Nothing here rewrites what was typed. There is no auto-close, no auto-correct and no token
 * atomicity -- a token typed by hand behaves exactly like one chosen from the menu, and
 * every key still reaches the field. The picker is an affordance, not a lock.
 *
 * Works on a wrapping `<textarea>` (ad copy) or a one-line `<input>` (a Create Studio text
 * layer). Where the menu hangs is the field's business: AdTokenTextarea measures the caret,
 * AdTokenInput anchors under the input.
 */

type Field = HTMLTextAreaElement | HTMLInputElement;

/**
 * Edits made to the field from outside it -- the "Use [TERM]" fix beside the field (MPN-1930).
 *
 * `edit` takes the field's current text and returns the new text and where the caret goes,
 * or null to leave it. It goes the same way an inserted token does: through `onValueChange`,
 * with the field focused and the caret placed once the render lands. So it is an edit like
 * any keystroke, and it saves when the field blurs, like any keystroke.
 */
export type AdTokenFieldHandle = {
    edit: (change: (value: string) => { value: string; caret: number } | null) => void;
};

/** The caller's own handlers, which the picker's run alongside. */
type OwnHandlers<F extends Field> = {
    onKeyDown?: (e: KeyboardEvent<F>) => void;
    onKeyUp?: (e: KeyboardEvent<F>) => void;
    onClick?: (e: MouseEvent<F>) => void;
    onBlur?: (e: FocusEvent<F>) => void;
};

export function useAdTokenPicker<F extends Field>({ tokens, value, onValueChange, ref }: {
    /** The vocabulary, already loaded. Empty means the picker never opens. */
    tokens: AdToken[];
    value: string;
    /** The text after an edit, whether it was typed or inserted from the menu. */
    onValueChange: (value: string) => void;
    ref: ForwardedRef<AdTokenFieldHandle>;
}) {
    const fieldRef = useRef<F | null>(null);
    const [context, setContext] = useState<AdTokenContext | null>(null);
    const [active, setActive] = useState(0);
    /**
     * The bracket the writer dismissed with Esc. It stays a literal `[` and the menu does not
     * reopen for it, which is the only way to type a bracket that is not a token -- there is
     * no escape syntax.
     */
    const dismissedAt = useRef(-1);
    /** Where the caret goes once React has rendered an inserted token. */
    const pendingCaret = useRef<number | null>(null);

    const matches: AdTokenMatch[] = useMemo(
        () => (context ? matchAdTokens(tokens, context.query) : []),
        [tokens, context],
    );
    const open = context !== null;

    const close = (dismissed: number) => {
        dismissedAt.current = dismissed;
        setContext(null);
    };

    /** Reopen, move or close the menu to match where the caret now is. */
    const syncContext = (opening: boolean) => {
        const field = fieldRef.current;
        if (!field || tokens.length === 0) return;

        const next = readAdTokenContext(field.value, field.selectionStart ?? 0, field.selectionEnd ?? 0);
        if (!next) {
            close(-1);
            return;
        }
        if (next.start === dismissedAt.current) {
            // Still inside the bracket Esc dismissed; leave it dismissed.
            setContext(null);
            return;
        }
        // Moving the caret to a different bracket is not a reason to open the menu there --
        // only typing is. Otherwise clicking into old copy would pop a menu unasked.
        if (!opening && next.start !== context?.start) {
            close(-1);
            return;
        }
        dismissedAt.current = -1;
        setContext((current) => (current?.start === next.start && current.query === next.query ? current : next));
        setActive(0);
    };

    // An inserted token changes the text through the parent, so the caret can only be placed
    // once that render has landed.
    useLayoutEffect(() => {
        const position = pendingCaret.current;
        if (position === null) return;
        pendingCaret.current = null;
        const field = fieldRef.current;
        if (!field) return;
        field.focus();
        field.setSelectionRange(position, position);
    }, [value]);

    const insert = (index: number) => {
        const field = fieldRef.current;
        const match = matches[index];
        if (!field || !context || !match) return;

        const next = insertAdToken(field.value, field.selectionStart ?? 0, context.start, match.token.token);
        pendingCaret.current = next.caret;
        close(-1);
        onValueChange(next.value);
    };

    useImperativeHandle(ref, () => ({
        edit: (change) => {
            const field = fieldRef.current;
            const next = field && change(field.value);
            if (!next) return;
            pendingCaret.current = next.caret;
            close(-1);
            onValueChange(next.value);
        },
    }));

    /** The props the field needs, with the caller's own handlers composed in. */
    const fieldProps = (own: OwnHandlers<F>) => ({
        "aria-autocomplete": "list" as const,
        "aria-expanded": open,
        onChange: (e: { target: F }) => {
            onValueChange(e.target.value);
            syncContext(true);
        },
        onKeyDown: (e: KeyboardEvent<F>) => {
            own.onKeyDown?.(e);
            if (!open) return;
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                if (matches.length === 0) return;
                e.preventDefault();
                setActive((i) => (i + (e.key === "ArrowDown" ? 1 : matches.length - 1)) % matches.length);
            } else if (e.key === "Enter" || e.key === "Tab") {
                if (matches.length === 0) return;
                e.preventDefault();
                insert(active);
            } else if (e.key === "Escape") {
                e.preventDefault();
                // The ad copy editor is a MUI Dialog, which closes on a bubbling Escape. With
                // the menu open Escape belongs to the menu: dismissing a suggestion must not
                // throw the writer out of the ad they are editing. With the menu closed it
                // bubbles as it always has.
                e.stopPropagation();
                close(context ? context.start : -1);
            }
        },
        onKeyUp: (e: KeyboardEvent<F>) => {
            own.onKeyUp?.(e);
            // The menu's own keys already did their work; anything else may have moved the
            // caret out of the bracket.
            if (["ArrowDown", "ArrowUp", "Enter", "Tab", "Escape"].includes(e.key)) return;
            if (open) syncContext(false);
        },
        onClick: (e: MouseEvent<F>) => {
            own.onClick?.(e);
            if (open) syncContext(false);
        },
        onBlur: (e: FocusEvent<F>) => {
            close(-1);
            own.onBlur?.(e);
        },
    });

    return { fieldRef, context, matches, active, setActive, open, insert, fieldProps };
}
